import { useToast } from '@chakra-ui/react';
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import {
  type AiScorePutRequest,
  type CandidateDetailResponse,
  type EmotionResponse,
  InterviewService,
  type PutCandidateFeedbackRequest,
  type PutCandidateListTypeRequest,
  type PutCandidateStatusRequest,
  type RateCandidateRequest,
  type ShareIndexModelPagedResponseDto,
  type SuggestionResponse,
} from '@/services/api';

export const adminCandidateKeys = {
  base: () => ['adminCandidate'] as const,

  getInterviewCandidates: (params: InterviewCandidateParams) =>
    [
      ...adminCandidateKeys.base(),
      'getAllInterviewsCandidates',
      params,
    ] as const,
  getInterviewCandidatesSuggestions: (params: InterviewSuggestionsParams) =>
    [
      ...adminCandidateKeys.base(),
      'getAllInterviewsCandidatesSuggestions',
      params,
    ] as const,
  getInterviewCandidatesEmotions: (params: InterviewCandidateEmotionsParams) =>
    [...adminCandidateKeys.base(), 'getAllInterviewsEmotions', params] as const,
  getCompanyShares: (params: CompanySharesParams) =>
    [...adminCandidateKeys.base(), 'getCompanyShares', params] as const,
};

type InterviewCandidateParams = {
  interviewsId: string;
  candidateId: string;
};

export const useGetInterviewCandidateQuery = (
  params: InterviewCandidateParams,
  options?: Partial<UseQueryOptions<CandidateDetailResponse, AxiosError>>
) => {
  return useQuery<CandidateDetailResponse, AxiosError>({
    queryKey: adminCandidateKeys.getInterviewCandidates(params),
    queryFn: () =>
      InterviewService.getInterviewsCandidatesById(params, {
        ...options?.axiosRequestConfig,
      }),
    ...options,
  });
};

type InterviewSuggestionsParams = {
  interviewsId: string;
  candidateId: string;
};

export const useGetInterviewSuggestionsQuery = (
  params: InterviewSuggestionsParams,
  options?: Partial<UseQueryOptions<SuggestionResponse, AxiosError>>
) => {
  return useQuery<SuggestionResponse, AxiosError>({
    queryKey: adminCandidateKeys.getInterviewCandidatesSuggestions(params),
    queryFn: () =>
      InterviewService.getInterviewsCandidatesSuggestionsById(params, {
        ...options?.axiosRequestConfig,
      }),
    ...options,
  });
};

export const useUpdateCandidateFeedbackMutation = (
  options?: UseMutationOptions<
    void,
    AxiosError,
    {
      interviewsId: string;
      candidateId: string;
      body: PutCandidateFeedbackRequest;
    }
  >
) => {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    AxiosError,
    {
      interviewsId: string;
      candidateId: string;
      body: PutCandidateFeedbackRequest;
    }
  >({
    mutationFn: async ({ body, interviewsId, candidateId }) => {
      return InterviewService.putInterviewsCandidatesFeedbackById(
        {
          interviewsId,
          candidateId,
          body,
        },
        options?.axiosRequestConfig
      );
    },
    async onMutate(variables) {
      const queryKey = adminCandidateKeys.getInterviewCandidates({
        candidateId: variables.candidateId,
        interviewsId: variables.interviewsId,
      });

      await queryClient.cancelQueries({
        queryKey,
      });

      const previousCandidate =
        queryClient.getQueryData<CandidateDetailResponse>(queryKey);

      queryClient.setQueryData<CandidateDetailResponse>(queryKey, (old) => {
        if (!old || !old.candidate) return;

        return {
          ...old,
          candidate: {
            ...old.candidate,
            feedback: variables.body.feedback,
          },
        } satisfies CandidateDetailResponse;
      });

      return { previousCandidate };
    },
    ...options,
  });
};

export const useRateCandidateRequestMutation = (
  options?: UseMutationOptions<
    void,
    AxiosError,
    { interviewsId: string; candidateId: string; body: RateCandidateRequest },
    { previousCandidate?: CandidateDetailResponse }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    AxiosError,
    { interviewsId: string; candidateId: string; body: RateCandidateRequest },
    { previousCandidate?: CandidateDetailResponse }
  >({
    mutationFn: async ({ body, interviewsId, candidateId }) => {
      return InterviewService.putInterviewsCandidatesRateById(
        {
          interviewsId,
          candidateId,
          body,
        },
        options?.axiosRequestConfig
      );
    },
    ...options,
    async onMutate(variables) {
      const queryKey = adminCandidateKeys.getInterviewCandidates({
        candidateId: variables.candidateId,
        interviewsId: variables.interviewsId,
      });

      await queryClient.cancelQueries({
        queryKey,
      });

      const previousCandidate =
        queryClient.getQueryData<CandidateDetailResponse>(queryKey);

      queryClient.setQueryData<CandidateDetailResponse>(queryKey, (old) => {
        if (!old || !old.candidate) return;

        return {
          ...old,
          candidate: {
            ...old.candidate,
            rating: variables.body.rating,
          },
        } satisfies CandidateDetailResponse;
      });

      return { previousCandidate };
    },
    onError: (_, variables, context) => {
      const queryKey = adminCandidateKeys.getInterviewCandidates(variables);
      queryClient.setQueryData(queryKey, context?.previousCandidate);
    },
    onSettled: (_, __, variables) => {
      const queryKey = adminCandidateKeys.getInterviewCandidates(variables);
      queryClient.invalidateQueries({ queryKey });
    },
  });
};

export type EditCandidateAiScoreParams = {
  interviewId: string;
  candidateId: string;
  questionId: string;
  body?: AiScorePutRequest;
};

export const useEditCandidateAiScoreMutation = (
  options?: UseMutationOptions<void, AxiosError, EditCandidateAiScoreParams>
) => {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, EditCandidateAiScoreParams>({
    mutationFn: async (variables) => {
      return InterviewService.putInterviewsCandidatesQuestionsAiScoreById(
        variables
      );
    },
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: adminCandidateKeys.getInterviewCandidates({
          candidateId: variables.candidateId,
          interviewsId: variables.interviewId,
        }),
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export type SetCandidateSuitabilityParams = {
  interviewsId: string;
  candidateId: string;
};

export const useSetCandidateSuitabilityToPositiveMutation = (
  options?: UseMutationOptions<void, AxiosError, SetCandidateSuitabilityParams>
) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation(['interview']);

  return useMutation<void, AxiosError, SetCandidateSuitabilityParams>({
    mutationFn: async (data) => {
      return InterviewService.putInterviewCandidateStatusPositiveById(data);
    },
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: adminCandidateKeys.getInterviewCandidates({
          candidateId: variables.candidateId,
          interviewsId: variables.interviewsId,
        }),
      });

      toast({
        title: t('interview:videoInterview.candidateSuitabilityAppliedSuccess'),
        status: 'success',
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useSetCandidateSuitabilityToNegativeMutation = (
  options?: UseMutationOptions<void, AxiosError, SetCandidateSuitabilityParams>
) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation(['interview']);

  return useMutation<void, AxiosError, SetCandidateSuitabilityParams>({
    mutationFn: async (data) => {
      return InterviewService.putInterviewCandidateStatusNegativeById(data);
    },
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: adminCandidateKeys.getInterviewCandidates({
          candidateId: variables.candidateId,
          interviewsId: variables.interviewsId,
        }),
      });

      toast({
        title: t('interview:videoInterview.candidateSuitabilityAppliedSuccess'),
        status: 'success',
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
};

export type ChangeCandidateEvaluationParams = {
  interviewsId: string;
  candidateId: string;
  body: PutCandidateStatusRequest;
};

export const useChangeCandidateEvaluationMutation = (
  options?: UseMutationOptions<
    void,
    AxiosError,
    ChangeCandidateEvaluationParams
  >
) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation(['interview']);

  return useMutation<void, AxiosError, ChangeCandidateEvaluationParams>({
    mutationFn: async (data) => {
      return InterviewService.putInterviewsCandidatesStatusById(data);
    },
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: adminCandidateKeys.getInterviewCandidates({
          candidateId: variables.candidateId,
          interviewsId: variables.interviewsId,
        }),
      });

      toast({
        title: t('interview:videoInterview.candidateSuitabilityAppliedSuccess'),
        status: 'success',
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
};

export type ReanalyzeCandidateQuestionRequest = {
  interviewsId: string;
  candidateId: string;
  questionId: string;
};

export const useReanalyzeCandidateQuestionMutation = (
  options?: UseMutationOptions<
    void,
    AxiosError,
    ReanalyzeCandidateQuestionRequest
  >
) => {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, ReanalyzeCandidateQuestionRequest>({
    mutationFn: async (variables) => {
      return InterviewService.putInterviewsCandidatesQuestionsReanalyzeById(
        variables
      );
    },
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: adminCandidateKeys.getInterviewCandidates({
          candidateId: variables.candidateId,
          interviewsId: variables.interviewsId,
        }),
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

type InterviewCandidateEmotionsParams = {
  interviewsId: string;
  candidateId: string;
  questionId: string;
};

export const useGetInterviewCandidateEmotionsByIdQuery = (
  params: InterviewCandidateEmotionsParams,
  options?: Partial<UseQueryOptions<EmotionResponse, AxiosError>>
) => {
  return useQuery<EmotionResponse, AxiosError>({
    queryKey: adminCandidateKeys.getInterviewCandidatesEmotions(params),
    queryFn: () =>
      InterviewService.getInterviewsCandidatesQuestionEmotionsById(params, {
        ...options?.axiosRequestConfig,
      }),
    ...options,
  });
};

type CompanySharesParams = {
  sharedCandidateEmail?: string;
  sharedCandidateFullName?: string;
  sharedInterviewTitle?: string;
  sharedInterviewId?: string;
  sharedPersonEmail?: string;
  sharedPersonFullName?: string;
  sharingPersonFullName?: string;
  expirationDateFrom?: string;
  expirationDateTo?: string;
  query?: string;
  isSystemUser?: boolean;
  size?: number;
  page?: number;
};

export const useGetCompanySharesQuery = (
  params: CompanySharesParams,
  options?: Partial<
    UseQueryOptions<ShareIndexModelPagedResponseDto, AxiosError>
  >
) => {
  return useQuery<ShareIndexModelPagedResponseDto, AxiosError>({
    queryKey: adminCandidateKeys.getCompanyShares(params),
    queryFn: () =>
      InterviewService.getCompanyShares(params, {
        ...options?.axiosRequestConfig,
      }),
    ...options,
  });
};

export type UpdateCandidateListTypeParams = {
  interviewsId: string;
  candidateId: string;
  body: PutCandidateListTypeRequest;
};

export const useUpdateCandidateListTypeMutation = (
  options?: UseMutationOptions<void, AxiosError, UpdateCandidateListTypeParams>
) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation(['candidate']);

  return useMutation<void, AxiosError, UpdateCandidateListTypeParams>({
    mutationFn: async (data) => {
      return InterviewService.putInterviewsCandidatesListTypeById(data);
    },
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: adminCandidateKeys.getInterviewCandidates({
          candidateId: variables.candidateId,
          interviewsId: variables.interviewsId,
        }),
      });

      toast({
        title: t('candidate:listTypeUpdatedSuccess'),
        status: 'success',
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
};
